import React from 'react';

const RefundPolicy = () => {
    return (
        <section className="refund-policy-section py-80 bg-gray-50 position-relative">
            <style>{`
                .policy-card {
                    background: #ffffff;
                    border: 1px solid #EFEFEF;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
                    transition: all 0.3s ease;
                }
                .policy-card:hover {
                    box-shadow: 0 8px 30px rgba(0,0,0,0.06);
                }
                .policy-icon-box {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: #EAF8EE;
                    color: var(--main-600, #2FA84F);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    flex-shrink: 0;
                }
                .policy-warning-box {
                    background: #FFFDF5;
                    border-left: 4px solid #F59E0B;
                    padding: 16px 20px;
                    border-radius: 0 12px 12px 0;
                    margin: 16px 0;
                }
                .policy-notice-box {
                    background: #F8FDF9;
                    border-left: 4px solid var(--main-600, #2FA84F);
                    padding: 16px 20px;
                    border-radius: 0 12px 12px 0;
                    margin: 16px 0;
                }
                .policy-bullet-list {
                    padding-left: 20px;
                    margin-bottom: 16px;
                }
                .policy-bullet-list li {
                    margin-bottom: 8px;
                    color: #4A5568;
                    line-height: 1.65;
                }
            `}</style>

            <div className="container container-lg">
                {/* Header Banner */}
                <div className="text-center mb-48 wow fadeInUp" data-wow-duration="0.6s">
                    <span className="text-main-600 fw-semibold text-uppercase mb-12 d-inline-block px-16 py-6 rounded-pill bg-main-50" style={{ letterSpacing: '1px', fontSize: 13 }}>
                        Returns &amp; Refunds
                    </span>
                    <h2 className="fw-bold mb-16">Cancellation, Return &amp; Refund Policy</h2>
                    <p className="text-gray-600 max-w-700 mx-auto" style={{ fontSize: 16, lineHeight: 1.7 }}>
                        Understand our return eligibility, food safety guidelines, cancellation terms, and refund processing for Healthy Home products.
                    </p>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="d-flex flex-column gap-32">

                            {/* Section 1: Food Safety Return Restrictions */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-shield-warning" />
                                    </div>
                                    <h4 className="fw-bold mb-0">1. Food Safety &amp; Hygiene Restrictions</h4>
                                </div>
                                <div className="policy-warning-box mb-16">
                                    <p className="mb-0 text-gray-800 fw-medium text-sm">
                                        <strong>Strict Food Safety Notice:</strong> Because Healthy Home sells consumable food products including cold-pressed edible oils, traditional sweeteners, and salts, returns are strictly restricted for reasons of public health, hygiene, and product integrity.
                                    </p>
                                </div>
                                <p className="text-gray-700 mb-0" style={{ lineHeight: 1.8 }}>
                                    We do not accept general returns or exchanges once food items are delivered, unless the condition falls under our eligible exceptions detailed below.
                                </p>
                            </div>

                            {/* Section 2: Eligible Exceptions */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-check-circle" />
                                    </div>
                                    <h4 className="fw-bold mb-0">2. Eligible Exceptions for Return &amp; Refund</h4>
                                </div>
                                <p className="text-gray-700 mb-12" style={{ lineHeight: 1.8 }}>
                                    Customers should contact us promptly upon receipt of their order if they receive:
                                </p>
                                <ul className="policy-bullet-list">
                                    <li><strong>Damaged / Leaking Product:</strong> Bottle breakage, leakage, or defective seal upon delivery.</li>
                                    <li><strong>Incorrect Item / Variant:</strong> A product or size variant materially different from what was ordered.</li>
                                    <li><strong>Expired / Defective Batch:</strong> Any quality defect identified immediately upon unboxing.</li>
                                </ul>
                                <div className="policy-notice-box">
                                    <p className="mb-0 text-gray-800 text-sm">
                                        <strong>Evidence Required:</strong> We may request clear photographs, unboxing videos, packaging details, and the batch number on the label before approving a return or refund request.
                                    </p>
                                </div>
                            </div>

                            {/* Section 3: Order Cancellation by Healthy Home or Customer */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-x-circle" />
                                    </div>
                                    <h4 className="fw-bold mb-0">3. Order Cancellation &amp; Prepaid Refunds</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    An order is considered accepted after successful order confirmation. We reserve the right to cancel or refuse an order due to product unavailability, incorrect pricing/information, payment authentication failure, suspected fraudulent transactions, or unserviceable delivery restrictions.
                                </p>
                                <div className="policy-notice-box">
                                    <p className="mb-0 text-gray-800 text-sm">
                                        <strong>Prepaid Order Cancellation:</strong> If a prepaid order is cancelled by Healthy Home, the eligible amount paid will be fully refunded to the customer through the applicable original payment method.
                                    </p>
                                </div>
                            </div>

                            {/* Section 4: Product Storage Disclaimer */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-thermometer-cold" />
                                    </div>
                                    <h4 className="fw-bold mb-0">4. Product Storage and Usage Disclaimer</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    Customers must follow the storage and handling instructions mentioned on the product packaging (e.g., storing unrefined oils in a cool, dry place away from heat and direct sunlight).
                                </p>
                                <p className="text-gray-700 mb-0" style={{ lineHeight: 1.8 }}>
                                    Healthy Home will not be responsible or liable for deterioration, rancidity, or spoilage caused by improper post-delivery storage, handling, or use.
                                </p>
                            </div>

                            {/* Section 5: Refund Mode & Timelines */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-arrow-counter-clockwise" />
                                    </div>
                                    <h4 className="fw-bold mb-0">5. Refund Mode &amp; Processing Timelines</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    Once an eligible return or refund request is verified and approved:
                                </p>
                                <ul className="policy-bullet-list">
                                    <li><strong>Original Payment Method:</strong> Refunds are credited back to the original source of payment (Bank Account, UPI, or Card).</li>
                                    <li><strong>Processing Time:</strong> Refunds are initiated within 3–5 business days and may take 5–7 banking days to reflect in your account, depending on your bank / card issuer.</li>
                                </ul>
                            </div>

                            {/* Section 6: Contact Information */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s" style={{ background: 'linear-gradient(135deg, #F8FDF9 0%, #FFFFFF 100%)', border: '1px solid #CDE8D5' }}>
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box" style={{ background: 'var(--main-600, #2FA84F)', color: '#FFFFFF' }}>
                                        <i className="ph ph-envelope-open" />
                                    </div>
                                    <div>
                                        <h4 className="fw-bold mb-4">6. Contact for Returns &amp; Refund Assistance</h4>
                                        <span className="text-sm text-gray-600">Healthy Home &bull; healthyhomeonline.in</span>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Phone Support</p>
                                        <a href="tel:9449818329" className="text-main-600 fw-semibold text-md">+91 9449818329</a>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Official Website</p>
                                        <a href="https://healthyhomeonline.in" target="_blank" rel="noopener noreferrer" className="text-main-600 fw-semibold text-md">healthyhomeonline.in</a>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Location</p>
                                        <span className="text-gray-900 fw-semibold text-md">Bengaluru, Karnataka, India</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RefundPolicy;
